/**
 * Line-crossing list URLs, preset-aware (ENG-1610).
 *
 * `LineCrossing.preset` is a nullable FK in actuate_admin: `null` means a
 * camera-wide "Default" line, anything else means the line belongs to a named
 * preset ("configuration"). A bare `GET /line_crossing/?camera=<id>` returns
 * EVERY line for the camera, preset-scoped ones included, so camera-wide
 * surfaces must ask for `preset__isnull=true` and preset surfaces for exactly
 * their preset (`preset=<id>`) — otherwise preset lines leak into the
 * camera-wide preview and editor, where they can be deleted or rewritten as
 * default lines. Both filters exist since actuate_admin PR #2894
 * (`LineCrossingViewSet.filterset_fields = {"preset": ["exact", "isnull"]}`).
 */
export const buildLineCrossingsListPath = (
  cameraId: number | string,
  presetId?: number | null
): string => {
  const numericPresetId = presetId == null ? NaN : Number(presetId);
  if (Number.isFinite(numericPresetId)) {
    return `/line_crossing/?camera=${cameraId}&preset=${numericPresetId}`;
  }
  return `/line_crossing/?camera=${cameraId}&preset__isnull=true`;
};

/**
 * Preset id carried on the Edit Line Crossings route as `?preset=<id>` (the
 * Site About camera row passes its selected flex preset this way). Returns
 * `null` for no/invalid param, which means the camera-wide Default configuration.
 */
export const parseLineCrossingsPresetIdFromHref = (href: string): number | null => {
  try {
    const search = new URL(href, "http://localhost").search;
    const raw = new URLSearchParams(search).get("preset");
    if (raw == null || raw.trim() === "") {
      return null;
    }
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

/** `?preset=<id>` suffix for links into the Edit Line Crossings page (empty for Default). */
export const lineCrossingsPresetSearch = (presetId: number | null | undefined): string =>
  presetId != null && Number.isFinite(Number(presetId)) ? `?preset=${presetId}` : "";
